<template>
    <div class="sub-modal-container rounded-lg">
        <div class="main-container">
            <div class="modal-header">
                <h2 class="modal-title">
                    <svg class="w-5 h-5 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
                    </svg>
                    Refund License
                </h2>
                <button class="close-btn" @click="closeDialog">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                </button>
            </div>
            <div class="divider"></div>
            
            <div class="stepper-wrapper">
                <Stepper :steps="stepperSteps" :current-step="step">
                    <template #default="{ currentStep }">
                        <div v-if="currentStep === 1">
                            <refund-feedback-block 
                                :license="license" 
                                @feedback="setFeedback" 
                                @goToPage="goToPage"
                                @close="closeDialog" 
                            />
                        </div>

                        <div v-else-if="currentStep === 2">
                            <refund-details-block 
                                :license="license"
                                @goToPage="goToPage" 
                                @close="closeDialog" 
                            />
                        </div>

                        <div v-else-if="currentStep === 3">
                            <refund-financial-block 
                                v-if="!showOfferSuccess && !showCancelAutoRenew" 
                                :license="license"
                                @close="closeDialog" 
                                :feedback="feedback" 
                                @goToPage="goToPage" 
                                @tookOffer="tookOfferSuccess"
                                @cancelAutoRenew="cancelAutoRenew" 
                            />

                            <refund-cancel-auto-renew 
                                v-if="showCancelAutoRenew && step === 3" 
                                :license="license"
                                @close="closeDialog" 
                            />
                        </div>

                        <div v-else-if="currentStep === 4">
                            <refund-review-block 
                                v-if="feedback && !showPendingTermination && !showCancelAutoRenew && !showRefundError" 
                                :license="license"
                                @close="closeDialog" 
                                :feedback="feedback" 
                                @terminationPending="terminatePending"
                                @cancelAutoRenew="cancelAutoRenew" 
                                @goToPage="goToPage"
                                @refundError="handleRefundError"
                            />
                            <refund-pending-termination 
                                v-if="showPendingTermination" 
                                :license="license" 
                                @close="closeDialog"
                                :amount="refundAmount"
                                :feedback="feedback" 
                            />
                            <refund-cancel-auto-renew 
                                v-if="showCancelAutoRenew && step === 4" 
                                :license="license"
                                @close="closeDialog" 
                            />
                            <refund-error-block
                                v-if="showRefundError"
                                :license="license"
                                :error-message="errorMessage"
                                @close="closeDialog"
                                @goToPage="goToPage"
                            />
                        </div>
                    </template>
                </Stepper>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { ModalNames } from '~/types/modals';

// Import refund components (these will need to be created)
import Stepper from '~/components/ui/Stepper.vue';
import RefundCancelAutoRenew from './Components/RefundCancelAutoRenew.vue';
import RefundDetailsBlock from './Components/RefundDetailsBlock.vue';
import RefundErrorBlock from './Components/RefundErrorBlock.vue';
import RefundFeedbackBlock from './Components/RefundFeedbackBlock.vue';
import RefundFinancialBlock from './Components/RefundFinancialBlock.vue';
import RefundPendingTermination from './Components/RefundPendingTermination.vue';
import RefundReviewBlock from './Components/RefundReviewBlock.vue';

const dialogRef = getDialogRef(ModalNames.REFUND_LICENSE);

// Reactive data
const step = ref(1);
const showOfferSuccess = ref(false);
const showCancelAutoRenew = ref(false);
const showPendingTermination = ref(false);
const showRefundError = ref(false);
const feedback = ref<{ reason?: string; additionalFeedback?: string }>({});
const refundAmount = ref(0);
const errorMessage = ref('');

// Stepper configuration
const stepperSteps = ['Feedback', 'Details', 'Options', 'Review'];

// Get license data from dialog
const license = computed(() => dialogRef.value?.data || null);



const closeDialog = () => {
    dialogRef?.value?.close();
};

const setFeedback = (newFeedback: any) => {
    feedback.value = newFeedback;
};

const tookOfferSuccess = () => {
    showCancelAutoRenew.value = false;
    showPendingTermination.value = false;
    showOfferSuccess.value = true;
};

const cancelAutoRenew = () => {
    showOfferSuccess.value = false;
    showPendingTermination.value = false;
    showCancelAutoRenew.value = true;
};

const terminatePending = (amount: number) => {
    showOfferSuccess.value = false;
    showCancelAutoRenew.value = false;
    showPendingTermination.value = true;
    refundAmount.value = amount;
};

const handleRefundError = (error: string) => {
    showOfferSuccess.value = false;
    showCancelAutoRenew.value = false;
    showPendingTermination.value = false;
    showRefundError.value = true;
    errorMessage.value = error;
};

const goToPage = (number: number) => {
    if (number === 2) {
        showCancelAutoRenew.value = false;
        showPendingTermination.value = false;
        refundAmount.value = 0;
    }
    showOfferSuccess.value = false;
    showCancelAutoRenew.value = false;
    showRefundError.value = false;
    step.value = number;
};
</script>

<style lang="scss" scoped>
.sub-modal-container {
    @apply bg-white max-w-4xl mx-auto overflow-y-auto max-h-screen scale-90;
}

.main-container {
    @apply flex p-3 pt-3 flex-col gap-6;
}

.modal-header {
    @apply flex justify-between items-center;
    
    .modal-title {
        @apply text-xl font-semibold text-gray-900 flex items-center;
    }
    
    .close-btn {
        @apply w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors;
        
        i {
            @apply text-lg;
        }
    }
}

.divider {
    @apply h-px self-stretch bg-zinc-300;
}

.stepper-wrapper {
    @apply max-w-5xl mx-auto;
}
</style>
